/**
 * Job Operations
 *
 * Platform-agnostic job CRUD operations.
 * These functions can be used by both web and mobile apps.
 *
 * @module @prosperia/shared/data/operations/jobs
 */

import {
  getSupabase,
  getCompanyId as sharedGetCompanyId,
  handleSupabaseError,
  withCompanyFilter,
} from '../../api/supabase';

/**
 * Get all jobs for a company
 *
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID (optional, will be resolved if not provided)
 * @param {Function} options.getCompanyId - Function to get company ID from context
 * @returns {Promise<Array>} Array of jobs or empty array on error
 */
export async function getJobs(options = {}) {
  try {
    const supabase = getSupabase();
    let companyId = options.companyId;

    if (!companyId && options.getCompanyId) {
      companyId = await options.getCompanyId();
    }

    if (!companyId) {
      // Try to get from session
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.user) {
        companyId = await sharedGetCompanyId(session.user);
      }
    }

    if (!companyId) {
      console.log('[jobs] No company ID available for getJobs');
      return [];
    }

    const { data, error } = await supabase
      .from('jobs')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[jobs] Error fetching jobs:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('[jobs] getJobs error:', error);
    handleSupabaseError(error, 'fetch jobs');
    return [];
  }
}

/**
 * Get jobs for a specific client with optional caching
 *
 * @param {string} clientId - Client ID
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Object} options.user - User object for company ID resolution
 * @param {Function} options.getCachedOrRateLimited - Caching function (optional)
 * @returns {Promise<Array>} Array of jobs or empty array on error
 */
export async function getJobsByClient(clientId, options = {}) {
  try {
    const supabase = getSupabase();
    let companyId = options.companyId;

    if (!companyId && options.user) {
      companyId = await sharedGetCompanyId(options.user);
    }

    if (!companyId) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('User not authenticated');
      companyId = await sharedGetCompanyId(session.user);
    }

    if (!companyId) throw new Error('Company not available');

    // Fetch function for jobs by client
    const fetchJobs = async () => {
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('company_id', companyId)
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[jobs] Error fetching jobs by client:', error);
        throw error;
      }

      return data || [];
    };

    // Use caching if provided, otherwise fetch directly
    if (options.getCachedOrRateLimited) {
      const cacheKey = `jobs_client_${clientId}_${companyId}`;
      return await options.getCachedOrRateLimited(cacheKey, fetchJobs);
    }

    return await fetchJobs();
  } catch (error) {
    console.error('[jobs] getJobsByClient error:', error);
    handleSupabaseError(error, 'fetch jobs by client');
    return [];
  }
}

/**
 * Get a single job by ID
 *
 * @param {string} id - Job ID
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Object} options.user - User object for company ID resolution
 * @returns {Promise<Object|null>} Job data or null on error
 */
export async function getJob(id, options = {}) {
  try {
    const supabase = getSupabase();
    let companyId = options.companyId;

    if (!companyId && options.user) {
      companyId = await sharedGetCompanyId(options.user);
    }

    if (!companyId) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('User not authenticated');
      companyId = await sharedGetCompanyId(session.user);
    }

    const { data, error } = await withCompanyFilter(
      supabase.from('jobs').select('*').eq('id', id),
      companyId
    ).single();

    if (error) throw error;
    return data;
  } catch (error) {
    handleSupabaseError(error, 'get job');
    return null;
  }
}

/**
 * Create a new job
 *
 * @param {Object} jobData - Job data to create
 * @param {Object} options - Options object (reserved for future use)
 * @returns {Promise<Object|null>} Created job data or null on error
 */
export async function createJob(jobData, options = {}) {
  try {
    const supabase = getSupabase();

    const { data, error } = await supabase.from('jobs').insert([jobData]).select().single();

    if (error) throw error;
    return data;
  } catch (error) {
    handleSupabaseError(error, 'create job');
    return null;
  }
}

/**
 * Update an existing job
 *
 * @param {string} id - Job ID
 * @param {Object} updates - Fields to update
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Object} options.user - User object for company ID resolution
 * @returns {Promise<Object|null>} Updated job data or null on error
 */
export async function updateJob(id, updates, options = {}) {
  try {
    const supabase = getSupabase();
    let companyId = options.companyId;

    if (!companyId && options.user) {
      companyId = await sharedGetCompanyId(options.user);
    }

    if (!companyId) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('User not authenticated');
      companyId = await sharedGetCompanyId(session.user);
    }

    const { data, error } = await withCompanyFilter(
      supabase.from('jobs').update(updates).eq('id', id),
      companyId
    )
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    handleSupabaseError(error, 'update job');
    return null;
  }
}

/**
 * Delete a job
 *
 * @param {string} id - Job ID
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Object} options.user - User object for company ID resolution
 * @returns {Promise<boolean>} True on success, false on error
 */
export async function deleteJob(id, options = {}) {
  try {
    const supabase = getSupabase();
    let companyId = options.companyId;

    if (!companyId && options.user) {
      companyId = await sharedGetCompanyId(options.user);
    }

    if (!companyId) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('User not authenticated');
      companyId = await sharedGetCompanyId(session.user);
    }

    const { error } = await withCompanyFilter(
      supabase.from('jobs').delete().eq('id', id),
      companyId
    );

    if (error) throw error;
    return true;
  } catch (error) {
    handleSupabaseError(error, 'delete job');
    return false;
  }
}
